<script setup lang="ts">
import { useDocumentVisibility } from '@vueuse/core'
import { computed, onMounted, reactive, ref } from 'vue'

import type {
  PathRecord,
} from '@/services/api/stastics.api'

import { DateRangePicker } from '@/components/date-picker'
import Page from '@/components/global-layout/basic-page.vue'
import { Icon } from '@/components/icon'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Tooltip,
  TooltipContent,
  TooltipProvider,
  TooltipTrigger,
} from '@/components/ui/tooltip'
import { useAmplitude } from '@/composables/use-amplitude'
import { useTeam } from '@/composables/use-team'
import { useTeamDateRange } from '@/composables/use-team-date-range'
import { useStatisticsStore } from '@/stores/statistics'
import { useUserStore } from '@/stores/user'

import AllAttributeDetailDialog from './components/all-attribute-detail-dialog.vue'
import AllFilterTags from './components/all-filter-tags.vue'
import ConversionPathFilter from './conversion-path/components/conversion-path-filter.vue'
import ConversionPathTable from './conversion-path/components/conversion-path-table.vue'

defineOptions({
  name: 'ConversionPathIndex',
})

const { track } = useAmplitude()

definePage({
  meta: {
    keepAlive: true,
  },
})

// Types
type SortDirection = 'ascending' | 'descending' | null

interface SearchSort {
  key: string
  sort: SortDirection
}

interface FilterItem {
  key?: string
  operation?: string
  values?: string[]
  display_values?: string[]
  selected_products?: Array<string | number>
}

interface SearchParams {
  date_start: string
  date_end: string
  timezone: string
  goal_code: string
  model_code: string
  dimension: 'channel' | 'campaign'
  window: number
  searchChannel: string
  searchInput: string
  filters: FilterItem[]
  sorts: SearchSort
  page: number
  page_size: number
}

// Composables
const userStore = useUserStore()
const statisticsStore = useStatisticsStore()
const { teamTimezone } = useTeam()
const { resolveDefaultRange } = useTeamDateRange()
const visibility = useDocumentVisibility()

// State
// 免费版默认显示今天
function getDefaultRange() {
  return resolveDefaultRange({ type: 'preset', preset: 'today' }, { endDateOffset: 1 })
}

const defaultRange = getDefaultRange()
const selectedDateRange = ref({ start: defaultRange.start, end: defaultRange.end })
const pickerDraftRange = ref({ ...selectedDateRange.value })

const tableKey = ref(`path-${Date.now()}`)
const tableData = ref<PathRecord[]>([])
const totalRecords = ref(0)
const sumData = ref<Record<string, any>>({})
const filterShowItems = ref<string[]>([])
const detailDialogRef = ref<InstanceType<typeof AllAttributeDetailDialog> | null>(null)
const showUpgradeDialog = ref(false)

// Dropdown data from store
const goalsSimpleListData = computed(() => statisticsStore.goalsSimpleList)
const modelSimpleListData = computed(() => statisticsStore.modelSimpleList)
const channelTypesData = computed(() => statisticsStore.basicChannelTypes)

// Dimension options
const dimensionTypes = [
  { label: 'Campaign', value: 'campaign' },
  { label: 'Channel', value: 'channel' },
]

// Search params
const searchParams = reactive<SearchParams>({
  date_start: selectedDateRange.value.start,
  date_end: selectedDateRange.value.end,
  timezone: defaultRange.timezone || teamTimezone.value,
  goal_code: '',
  model_code: '',
  dimension: 'campaign',
  window: 30,
  searchChannel: '',
  searchInput: '',
  filters: [],
  sorts: { key: 'conversions', sort: 'descending' },
  page: 1,
  page_size: 20,
})

// Mock Data Generation
const MOCK_TABLE_DATA: PathRecord[] = [
  {
    conversions: 15,
    conversion_value: 1154.1,
    aov: 107.13,
    time_diff: 43200, // 12h
    touch_points: 3,
    event_final_path: [
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Website traffic-Search', event_num: 1, event_conversions: 1.0 },
    ],
    _dimensionKey: 'mock-1',
  },
  {
    conversions: 3,
    conversion_value: 321.41,
    aov: 107.13,
    time_diff: 3600, // 1h
    touch_points: 1,
    event_final_path: [
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Display-1', event_num: 1, event_conversions: 0.2 },
      { event_type: 'facebook_ad', event_platform: 'facebook', event_campaign: 'Search & Display-CAM', event_num: 1, event_conversions: 0.2 },
      { event_type: 'tiktok_ad', event_platform: 'tiktok', event_campaign: 'Traffic202211091549...', event_num: 1, event_conversions: 0.2 },
      { event_type: 'other', event_platform: 'other', event_campaign: 'SOC-0815-1', event_num: 1, event_conversions: 0.2 },
      { event_type: 'tiktok_ad', event_platform: 'tiktok', event_campaign: 'Brand publicity-0322', event_num: 1, event_conversions: 0.2 },
    ],
    _dimensionKey: 'mock-2',
  },
  {
    conversions: 2,
    conversion_value: 100.9,
    aov: 50.45,
    time_diff: 3600,
    touch_points: 5,
    event_final_path: [
      { event_type: 'facebook_ad', event_platform: 'facebook', event_campaign: 'Traffic Campaign-CAM', event_num: 1, event_conversions: 1.0 },
    ],
    _dimensionKey: 'mock-3',
  },
  {
    conversions: 1,
    conversion_value: 71.41,
    aov: 71.41,
    time_diff: 3600,
    touch_points: 1,
    event_final_path: [
      { event_type: 'facebook_ad', event_platform: 'facebook', event_campaign: 'FBAD-1', event_num: 1, event_conversions: 0.111 },
      { event_type: 'referral', event_platform: 'other', event_campaign: 'facebook/referral', event_num: 1, event_conversions: 0.111 },
      { event_type: 'other', event_platform: 'other', event_campaign: 'Account-A', event_num: 1, event_conversions: 0.111 },
      { event_type: 'facebook_ad', event_platform: 'facebook', event_campaign: 'Standard Shopping-c...', event_num: 1, event_conversions: 0.111 },
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Website traffic-Searc...', event_num: 1, event_conversions: 0.111 },
      { event_type: 'tiktok_ad', event_platform: 'tiktok', event_campaign: 'Traffic202211091549...', event_num: 1, event_conversions: 0.111 },
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Search & Display-05...', event_num: 1, event_conversions: 0.111 },
      { event_type: 'tiktok_ad', event_platform: 'tiktok', event_campaign: 'Account-A', event_num: 1, event_conversions: 0.111 },
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Display-1', event_num: 1, event_conversions: 0.111 },
    ],
    _dimensionKey: 'mock-4',
  },
  {
    conversions: 1,
    conversion_value: 28.32,
    aov: 28.32,
    time_diff: 604800, // 7d
    touch_points: 9,
    event_final_path: [
      { event_type: 'direct', event_platform: 'other', event_campaign: '--/direct', event_num: 1, event_conversions: 1.0 },
    ],
    _dimensionKey: 'mock-5',
  },
  {
    conversions: 1,
    conversion_value: 51.5,
    aov: 51.5,
    time_diff: 3600,
    touch_points: 1,
    event_final_path: [
      { event_type: 'tiktok_ad', event_platform: 'tiktok', event_campaign: 'Brand publicity-0322...', event_num: 1, event_conversions: 0.333 },
      { event_type: 'google_ad', event_platform: 'google', event_campaign: 'Website traffic-Searc...', event_num: 2, event_conversions: 0.667 },
    ],
    _dimensionKey: 'mock-6',
  },
  {
    conversions: 1,
    conversion_value: 63.4,
    aov: 63.4,
    time_diff: 3600,
    touch_points: 3,
    event_final_path: [
      { event_type: 'email', event_platform: 'other', event_campaign: '--/email', event_num: 1, event_conversions: 1.0 },
    ],
    _dimensionKey: 'mock-7',
  },
  {
    conversions: 1,
    conversion_value: 63.4,
    aov: 63.4,
    time_diff: 3600,
    touch_points: 1,
    event_final_path: [
      { event_type: 'sm', event_platform: 'other', event_campaign: 'Zhihu/social', event_num: 1, event_conversions: 0.5 },
      { event_type: 'facebook_ad', event_platform: 'facebook', event_campaign: 'Spring Sale-C', event_num: 1, event_conversions: 0.5 },
    ],
    _dimensionKey: 'mock-8',
  },
] as any

// Initialize data
async function initializeData() {
  try {
    // Load goals list
    await statisticsStore.getGoalsSimpleList()
    if (goalsSimpleListData.value.length > 0 && !searchParams.goal_code) {
      searchParams.goal_code = goalsSimpleListData.value[0].code
    }

    // Load models list
    await statisticsStore.getModelSimpleList()
    if (modelSimpleListData.value.length > 1 && !searchParams.model_code) {
      searchParams.model_code = modelSimpleListData.value[modelSimpleListData.value.length - 2]?.code || modelSimpleListData.value[0]?.code
    }

    // Load channel types
    await statisticsStore.getTypes()

    // Set mock data
    tableData.value = MOCK_TABLE_DATA
    totalRecords.value = 100
    sumData.value = {
      conversions: 1234,
      conversion_value: 56789,
      aov: 123,
      time_diff: 48,
      touch_points: 3.5,
    }
  }
  catch (e) {
    console.error('Failed to initialize data:', e)
  }
}

onMounted(() => {
  initializeData()
  showUpgradeDialog.value = true
  track('explorer_conversion_path_upgrade_show')
})

// Handlers
function handleDateChange(range?: { start: string, end: string }) {
  // Mock change
  if (range) {
    selectedDateRange.value = range
    searchParams.date_start = range.start
    searchParams.date_end = range.end
  }
}

function handleSortChange({ prop, order }: { prop: string, order: SortDirection }) {
  // Mock sort
  if (!order) {
    searchParams.sorts = { key: 'conversions', sort: 'descending' }
  }
  else {
    searchParams.sorts = { key: prop, sort: order }
  }
}

function setPage(p: number) {
  // Mock pagination
  searchParams.page = p
}

function handlePageSizeChange(value: number) {
  searchParams.page_size = value
  searchParams.page = 1
}

function handleDimensionChange() {
  searchParams.searchChannel = ''
  searchParams.searchInput = ''
  searchParams.page = 1
}

function handleSearchInputBlur() {
  searchParams.page = 1
}

function handleRowClick(row: PathRecord) {
  // Disabled
}

function handleFilterChange(filters: any[], showItems: string[]) {
  searchParams.filters = filters as FilterItem[]
  filterShowItems.value = showItems
  searchParams.page = 1
}

function handleFilterTagClose(index: number) {
  searchParams.filters.splice(index, 1)
  filterShowItems.value.splice(index, 1)
  searchParams.page = 1
}

function handleRefresh() {
  // Mock refresh
}

function handleRestoreAccess() {
  track('explorer_conversion_path_upgrade_click')
  window.open(userStore.getSubscriptionPlanUrl(), '_blank')
}

// No data text
const noDataText = computed(() => {
  return 'No data found.'
})

const isTableLoading = ref(false)
</script>

<template>
  <div class="explorer-conversion-path-page cf-fullwidth bfsp-explorer-conversion-path-span-anchor min-h-screen bg-[--p-color-bg] px-[--p-space-600] md:px-[--p-space-1200] p-[--p-space-600]">
    <Page title="Conversion Path" description="Analyze customer conversion paths and touchpoints" class="h-full">
      <template #header-extra>
        <div class="flex items-center gap-2 pointer-events-none opacity-50">
          <Button variant="outline" size="sm" class="cta-secondary" @click="handleRefresh">
            <Icon name="lucide:refresh-cw" class="mr-1 h-4 w-4" />
            Refresh
          </Button>
        </div>
      </template>

      <!-- Filter Bar - Single Row Layout -->
      <div class="mb-6 flex flex-wrap items-center gap-3 pointer-events-none opacity-50">
        <!-- Conversion Goal -->
        <Select v-model="searchParams.goal_code">
          <SelectTrigger class="w-[140px]">
            <SelectValue placeholder="Conversion goal" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="goal in goalsSimpleListData"
              :key="goal.code"
              :value="goal.code"
            >
              {{ goal.name }}
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Date Range Picker -->
        <DateRangePicker
          v-model="pickerDraftRange"
          :timezone="teamTimezone"
          :end-date-offset="1"
          :is-plan-picker="true"
          auth-key="conversion_path"
          class="w-[200px]"
          @refresh="handleDateChange"
        />

        <!-- Dimension -->
        <Select v-model="searchParams.dimension" @update:model-value="handleDimensionChange">
          <SelectTrigger class="w-[120px]">
            <SelectValue placeholder="Dimension" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="dim in dimensionTypes"
              :key="dim.value"
              :value="dim.value"
            >
              {{ dim.label }}
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Channel Filter (when dimension is channel) -->
        <div v-if="searchParams.dimension === 'channel'" class="flex items-center gap-1">
          <Select v-model="searchParams.searchChannel">
            <SelectTrigger class="w-[140px]">
              <SelectValue placeholder="Select" />
            </SelectTrigger>
            <SelectContent>
              <SelectItem
                v-for="channel in channelTypesData"
                :key="channel.value"
                :value="channel.value"
              >
                {{ channel.text }}
              </SelectItem>
            </SelectContent>
          </Select>
          <Button
            v-if="searchParams.searchChannel"
            variant="ghost"
            size="icon"
            class="h-8 w-8 cta-secondary-inline"
            @click="searchParams.searchChannel = ''"
          >
            <Icon name="lucide:x" class="h-4 w-4" />
          </Button>
        </div>

        <!-- Campaign Search (when dimension is campaign) -->
        <Input
          v-if="searchParams.dimension === 'campaign'"
          v-model="searchParams.searchInput"
          placeholder="Search campaign"
          class="w-[160px]"
          @keyup.enter="handleSearchInputBlur"
          @blur="handleSearchInputBlur"
        >
          <template #prefix>
            <Icon name="lucide:search" class="h-4 w-4 text-muted-foreground" />
          </template>
        </Input>

        <!-- Attribution Model -->
        <Select v-model="searchParams.model_code">
          <SelectTrigger class="w-[140px]">
            <SelectValue placeholder="Attribution model" />
          </SelectTrigger>
          <SelectContent>
            <SelectItem
              v-for="model in modelSimpleListData"
              :key="model.code"
              :value="model.code"
            >
              {{ model.name }}
            </SelectItem>
          </SelectContent>
        </Select>

        <!-- Help Icon -->
        <TooltipProvider>
          <Tooltip>
            <TooltipTrigger>
              <Icon name="lucide:help-circle" class="h-4 w-4 text-muted-foreground" />
            </TooltipTrigger>
            <TooltipContent side="top" class="max-w-[300px]">
              <p>Switching attribution model will change the credit sent to each touchpoint. Direct traffic is always the last one to get credit.</p>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>

        <!-- Advanced Filter (only for purchase goal) -->
        <ConversionPathFilter
          v-if="searchParams.goal_code === 'x4w0tc0elo0co1n9eftdalo100h8dp6k'"
          :model-value="searchParams.filters"
          :channel-types="channelTypesData"
          @update:model-value="handleFilterChange"
        />
      </div>

      <!-- Filter Tags -->
      <div v-if="filterShowItems.length > 0" class="mb-6 rounded-[12px] border border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] p-4 shadow-[var(--p-shadow-100)]">
        <AllFilterTags
          :filters="filterShowItems"
          @close="handleFilterTagClose"
        />
      </div>

      <!-- Table View -->
      <div class="rounded-[12px] border border-[var(--p-color-border-subdued)] bg-[var(--p-color-bg-surface)] shadow-[var(--p-shadow-100)] pointer-events-none select-none">
        <Skeleton v-if="isTableLoading" class="m-4 h-[400px] w-full" />
        <template v-else>
          <ConversionPathTable
            :key="tableKey"
            :table-data="tableData"
            :sum-data="sumData"
            :dimension-type="searchParams.dimension"
            :page="{ current: searchParams.page, size: searchParams.page_size, total: totalRecords }"
            :search-params="searchParams"
            :no-data-text="noDataText"
            :channel-types="channelTypesData"
            :blur-data="true"
            @sort-change="handleSortChange"
            @row-click="handleRowClick"
          />

          <!-- Pagination -->
          <div v-if="totalRecords > 0" class="flex items-center justify-between border-t px-4 py-3">
            <div class="text-sm text-muted-foreground">
              Showing {{ (searchParams.page - 1) * searchParams.page_size + 1 }} to {{ Math.min(searchParams.page * searchParams.page_size, totalRecords) }} of {{ totalRecords }} results
            </div>
            <div class="flex items-center gap-2">
              <Button
                variant="outline"
                size="sm"
                class="cta-secondary-pagination"
                :disabled="searchParams.page <= 1"
                @click="setPage(searchParams.page - 1)"
              >
                Previous
              </Button>
              <span class="text-sm">Page {{ searchParams.page }} of {{ Math.ceil(totalRecords / searchParams.page_size) }}</span>
              <Button
                variant="outline"
                size="sm"
                class="cta-secondary-pagination"
                :disabled="searchParams.page >= Math.ceil(totalRecords / searchParams.page_size)"
                @click="setPage(searchParams.page + 1)"
              >
                Next
              </Button>
            </div>
          </div>
        </template>
      </div>

      <!-- Detail Dialog -->
      <AllAttributeDetailDialog
        ref="detailDialogRef"
        :search-params="{}"
        object-type="conversionPath"
      />
    </Page>

    <!-- Upgrade Dialog Overlay -->
    <div
      v-if="showUpgradeDialog"
      class="absolute inset-0 z-50 flex items-center justify-center bg-black/80"
    >
      <div
        class="bg-background relative grid w-full max-w-lg gap-4 rounded-[var(--p-border-radius-200)] border p-6 shadow-lg duration-200 sm:max-w-[425px]"
        @pointerdown.stop
        @click.stop
      >
        <div class="flex flex-col space-y-1.5 text-center sm:text-left">
          <h2 class="text-lg font-semibold leading-none tracking-tight text-left text-xl">
            Your attribution insights are paused
          </h2>
          <p class="text-sm text-muted-foreground text-left pt-2">
            Upgrade to restore access to the analytics modules that drive revenue decisions.
          </p>
        </div>
        <div class="flex flex-col-reverse sm:flex-row sm:justify-start sm:space-x-2">
          <Button
            class="w-full cta-primary-success"
            @click="handleRestoreAccess"
          >
            Restore full access
          </Button>
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped>
.blur-content {
  filter: blur(5px);
  user-select: none;
  pointer-events: none;
}

.explorer-conversion-path-page :deep([data-slot="button"]):focus-visible,
.explorer-conversion-path-page :deep([role="button"]):focus-visible,
.explorer-conversion-path-page :deep(button):focus-visible,
.explorer-conversion-path-page :deep(a[href]):focus-visible,
.explorer-conversion-path-page :deep(input):focus-visible,
.explorer-conversion-path-page :deep(select):focus-visible,
.explorer-conversion-path-page :deep(textarea):focus-visible,
.explorer-conversion-path-page :deep([tabindex]):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.explorer-conversion-path-page :deep(.cta-primary-success) {
  background: var(--p-color-bg-fill-success) !important;
  color: var(--p-color-text-brand-on-bg-fill) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
  height: 36px;
  border-radius: var(--p-border-radius-100);
  font-size: 13px;
  font-weight: 500;
  padding-left: var(--p-space-400);
  padding-right: var(--p-space-400);
}
.explorer-conversion-path-page :deep(.cta-primary-success):hover {
  background: var(--p-color-bg-fill-success-hover) !important;
  box-shadow: none !important;
  border: none !important;
  outline: none !important;
}
.explorer-conversion-path-page :deep(.cta-primary-success):active {
  background: var(--p-color-bg-fill-success-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(0, 52, 38, 0.25) inset !important;
  border: none !important;
  outline: none !important;
}
.explorer-conversion-path-page :deep(.cta-primary-success):focus-visible {
  border: none !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.explorer-conversion-path-page :deep(.cta-secondary) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  letter-spacing: 0 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-strong) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
  padding-left: var(--p-space-400) !important;
  padding-right: var(--p-space-400) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-strong) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-strong) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary):focus-visible {
  border-color: var(--p-color-border-strong) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.explorer-conversion-path-page :deep(.cta-secondary-inline) {
  border-radius: var(--p-border-radius-100) !important;
  background: transparent !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  outline: none !important;
  transition: background-color 100ms ease-out, box-shadow 100ms ease-out;
}
.explorer-conversion-path-page :deep(.cta-secondary-inline):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-inline):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-inline):focus-visible {
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.explorer-conversion-path-page :deep(.cta-secondary-pagination) {
  height: 32px !important;
  border-radius: var(--p-border-radius-100) !important;
  font-size: 13px !important;
  font-weight: 500 !important;
  background: var(--p-color-bg-fill) !important;
  color: var(--p-color-text) !important;
  box-shadow: none !important;
  border: 1px solid var(--p-color-border-strong) !important;
  outline: none !important;
  padding-left: var(--p-space-300) !important;
  padding-right: var(--p-space-300) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-pagination):hover {
  background: var(--p-color-bg-fill-hover) !important;
  box-shadow: none !important;
  border-color: var(--p-color-border-strong) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-pagination):active {
  background: var(--p-color-bg-fill-active) !important;
  box-shadow: 0px 1px 2px 0px rgba(26, 26, 26, 0.15) inset !important;
  border-color: var(--p-color-border-strong) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-pagination):focus-visible {
  border-color: var(--p-color-border-strong) !important;
  outline: none !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}
.explorer-conversion-path-page :deep(.cta-secondary-pagination):disabled {
  opacity: 0.5;
  cursor: not-allowed;
}

.explorer-conversion-path-page :deep(input) {
  height: 36px !important;
  border-radius: var(--p-border-radius-100) !important;
  background: var(--p-color-bg-fill) !important;
  border: 1px solid var(--p-color-border-subdued) !important;
  color: var(--p-color-text) !important;
  font-size: 13px !important;
}
.explorer-conversion-path-page :deep(input):focus-visible {
  outline: none !important;
  border-color: var(--p-color-bg-fill-success) !important;
  box-shadow: 0 0 0 2px var(--p-color-bg-surface), 0 0 0 5px rgba(4, 123, 93, 0.35) !important;
}

.explorer-conversion-path-page :deep([data-slot="table-header"]) {
  background: var(--p-color-bg-surface-secondary) !important;
  text-transform: uppercase !important;
  letter-spacing: 0.1em !important;
  font-size: 11px !important;
  font-weight: 500 !important;
  color: var(--p-color-text-secondary) !important;
}

.explorer-conversion-path-page :deep([data-slot="table-row"]) {
  background: var(--p-color-bg-surface) !important;
}
.explorer-conversion-path-page :deep([data-slot="table-row"]):hover {
  background: var(--p-color-bg-surface-secondary) !important;
}

.explorer-conversion-path-page :deep(.text-secondary),
.explorer-conversion-path-page :deep(.text-muted-foreground) {
  color: var(--p-color-text-secondary) !important;
}

.cf-fullwidth {
  width: calc(100% + 2 * var(--p-space-600)) !important;
  position: relative !important;
  left: calc(-1 * var(--p-space-600)) !important;
  max-width: none !important;
  padding-left: var(--p-space-600) !important;
  padding-right: var(--p-space-600) !important;
  box-sizing: border-box !important;
}
@media (min-width: 768px) {
  .cf-fullwidth {
    width: calc(100% + 2 * var(--p-space-1200)) !important;
    left: calc(-1 * var(--p-space-1200)) !important;
    padding-left: var(--p-space-1200) !important;
    padding-right: var(--p-space-1200) !important;
  }
}

.bfsp-explorer-conversion-path-span-anchor { width: calc(100% + 2* var(--p-space-600)) !important; position: relative !important; left: calc(-1 * var(--p-space-600)) !important; max-width: none !important; }
@media (min-width: 768px) { .bfsp-explorer-conversion-path-span-anchor { width: calc(100% + 2*var(--p-space-1200)) !important; left: calc(-1*var(--p-space-1200)) !important; } }
</style>
